import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, UserPlus } from "lucide-react";
import FinanceLayout from "@/components/FinanceLayout";
import { useWorkspace } from "@/context/WorkspaceContext";
import { api, formatApiError } from "@/lib/api";
import { SECTORS, NEEDS, toDatetimeLocal, fromDatetimeLocal } from "@/lib/constants";
import { PAYMENT_PREFS, FIXED_EXPENSE_ITEMS } from "@/lib/finance";
import { toast } from "sonner";

const label = "text-sm font-semibold text-slate-800 mb-2";
const inp = "w-full h-12 rounded-xl border border-slate-200 bg-slate-50/40 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500 placeholder:text-slate-400";

export default function RecurringJobNew() {
  const { workspace } = useWorkspace();
  const nav = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [extraSectors, setExtraSectors] = useState([]);
  const [f, setF] = useState({
    date: toDatetimeLocal(new Date().toISOString()),
    brand_name: "", contact_name: "", sector: "", sector_other: "",
    needs: [], amount: "", partial_payment: "", payment_day: "",
    phone: "", email: "", payment_pref: "", active: true,
    fixed_expenses: { aylik_cekim: null, aylik_reklamci: null, aylik_yazilim: null, aylik_diger: null },
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { (async () => {
    const [{ data: c }, { data: sx }] = await Promise.all([
      api.get("/finance/customers", { params: { workspace, active_only: true } }),
      api.get("/finance/lookups/sectors", { params: { workspace } }),
    ]);
    setCustomers(c); setExtraSectors(sx);
  })(); }, [workspace]);

  const pickCustomer = (brand) => {
    if (!brand) return;
    const c = customers.find((x) => x.brand_name === brand);
    if (!c) return;
    setF((p) => ({ ...p, brand_name: c.brand_name, contact_name: c.contact_name || p.contact_name,
      phone: c.phone || p.phone, email: c.email || p.email,
      sector: c.sector || p.sector, sector_other: c.sector_other || p.sector_other }));
  };

  const toggleNeed = (v) => setF((p) => ({ ...p, needs: p.needs.includes(v) ? p.needs.filter((x) => x !== v) : [...p.needs, v] }));
  const toggleFixed = (key) => setF((p) => ({ ...p, fixed_expenses: { ...p.fixed_expenses, [key]: p.fixed_expenses[key] == null ? "" : null } }));
  const setFixed = (key, v) => setF((p) => ({ ...p, fixed_expenses: { ...p.fixed_expenses, [key]: v } }));

  const submit = async (e) => {
    e.preventDefault(); setError("");
    if (!f.brand_name.trim() || !f.amount || !f.payment_day) return setError("Marka Adı, Anlaşma Bedeli ve Ödeme Günü zorunlu.");
    setSaving(true);
    try {
      if (f.sector === "diger" && f.sector_other.trim()) {
        await api.post("/finance/lookups/sectors", null, { params: { workspace, label: f.sector_other.trim() } });
      }
      const fixed = FIXED_EXPENSE_ITEMS.filter((it) => f.fixed_expenses[it.key] != null)
        .map((it) => ({ key: it.key, amount: parseFloat(f.fixed_expenses[it.key] || 0) }));
      await api.post("/finance/recurring-jobs", {
        workspace, date: fromDatetimeLocal(f.date) || new Date().toISOString(),
        brand_name: f.brand_name, contact_name: f.contact_name,
        sector: f.sector, sector_other: f.sector_other,
        needs: f.needs, amount: parseFloat(f.amount),
        partial_payment: parseFloat(f.partial_payment || 0),
        payment_day: parseInt(f.payment_day),
        phone: f.phone, email: f.email, payment_pref: f.payment_pref || null,
        active: f.active, fixed_expenses: fixed,
      });
      toast.success("Tekrar eden iş kaydedildi");
      nav("/finance/monthly-billing");
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <FinanceLayout>
      <div className="mb-6 flex items-center gap-3">
        <button onClick={() => nav(-1)} className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50"><ArrowLeft size={16} /></button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Yeni Tekrar Eden İş Ekleme</h1>
          <p className="text-sm text-slate-500 mt-1">Aylık anlaşma, sabit gider ve ödeme bilgilerini girin.</p>
        </div>
      </div>

      <form onSubmit={submit} className="rounded-2xl border border-slate-100 bg-white p-8" data-testid="rec-job-form">
        {customers.length > 0 && (
          <div className="mb-6 rounded-xl bg-emerald-50/40 border border-emerald-100 p-4">
            <div className="flex items-center justify-between gap-3">
              <div><div className="text-sm font-semibold text-emerald-800 flex items-center gap-2"><UserPlus size={14} /> Mevcut Müşteriden Seç</div>
                <div className="text-xs text-slate-500 mt-0.5">Formu daha önce eklediğiniz müşteri bilgileriyle doldur.</div>
              </div>
              <select onChange={(e) => pickCustomer(e.target.value)} data-testid="rj-picker" className="h-11 min-w-[240px] rounded-lg border border-slate-200 bg-white px-4 text-sm">
                <option value="">Müşteri seçin…</option>
                {customers.map((c) => <option key={c.id} value={c.brand_name}>{c.brand_name}</option>)}
              </select>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-x-8 gap-y-5">
          <div><div className={label}>Tarih</div><input type="datetime-local" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={inp} data-testid="rj-date" /></div>
          <div><div className={label}>Marka Adı</div><input value={f.brand_name} onChange={(e) => setF({ ...f, brand_name: e.target.value })} placeholder="Marka adı" className={inp} data-testid="rj-brand" /></div>
          <div><div className={label}>Yetkili Adı</div><input value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} placeholder="Yetkili adı" className={inp} data-testid="rj-contact" /></div>
          <div><div className={label}>Sektör</div>
            <select value={f.sector} onChange={(e) => setF({ ...f, sector: e.target.value, sector_other: e.target.value === "diger" ? f.sector_other : "" })} className={inp} data-testid="rj-sector">
              <option value="">Sektör seçiniz</option>
              {SECTORS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              {extraSectors.map((s) => <option key={s} value={`custom:${s}`}>{s}</option>)}
            </select>
            {f.sector === "diger" && (
              <input value={f.sector_other} onChange={(e) => setF({ ...f, sector_other: e.target.value })} placeholder="Yeni sektör adı (kaydedildiğinde listeye eklenir)" className={inp + " mt-2"} data-testid="rj-sector-other" />
            )}
          </div>
          <div className="col-span-2"><div className={label}>Çalışma Kapsamı</div>
            <div className="flex flex-wrap gap-2">{NEEDS.map((n) => { const on = f.needs.includes(n.value); return (
              <button key={n.value} type="button" onClick={() => toggleNeed(n.value)} data-testid={`rj-need-${n.value}`}
                className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${on ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{n.label}</button>
            );})}</div>
          </div>
          <div><div className={label}>Aylık Anlaşma Bedeli</div><input type="number" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="Tutar (TL)" className={inp} data-testid="rj-amount" /></div>
          <div><div className={label}>Kısmi Ödeme Tutarı (opsiyonel)</div><input type="number" step="0.01" value={f.partial_payment} onChange={(e) => setF({ ...f, partial_payment: e.target.value })} placeholder="Şu ana kadar alınan tutar" className={inp} data-testid="rj-partial" /></div>
          <div><div className={label}>Ödeme Günü</div>
            <select value={f.payment_day} onChange={(e) => setF({ ...f, payment_day: e.target.value })} className={inp} data-testid="rj-day">
              <option value="">Gün seçiniz</option>{Array.from({length:31},(_,i)=>i+1).map((d)=><option key={d} value={d}>{d}</option>)}
            </select></div>
          <div><div className={label}>Telefon</div><input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={inp} data-testid="rj-phone" /></div>
          <div><div className={label}>Mail</div><input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inp} data-testid="rj-email" /></div>
          <div><div className={label}>Ödeme Tercihi</div>
            <select value={f.payment_pref} onChange={(e) => setF({ ...f, payment_pref: e.target.value })} className={inp} data-testid="rj-pref">
              <option value="">Seçiniz</option>{PAYMENT_PREFS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select></div>
          <div><div className={label}>Durum</div>
            <div className="inline-flex bg-slate-100 rounded-full p-1">
              {[[true,"Aktif"],[false,"Pasif"]].map(([v,t])=>(<button key={t} type="button" onClick={()=>setF({...f,active:v})} className={`px-6 py-2 rounded-full text-sm font-semibold ${f.active===v?"bg-emerald-800 text-white":"text-slate-600"}`}>{t}</button>))}
            </div></div>
        </div>

        <div className="mt-8 rounded-xl bg-slate-50/60 border border-slate-100 p-6">
          <div className="text-sm font-semibold text-slate-900 mb-1">Sabit Gider (Aylık Tekrar Eder)</div>
          <div className="text-xs text-slate-500 mb-4">Bu iş için her ay tekrar eden sabit giderlerinizi ekleyin.</div>
          <div className="grid grid-cols-4 gap-4">
            {FIXED_EXPENSE_ITEMS.map((it) => {
              const val = f.fixed_expenses[it.key]; const on = val != null;
              return (
                <div key={it.key} className={`rounded-lg border ${on ? "border-emerald-300" : "border-slate-200"} bg-white p-3`}>
                  <label className="flex items-center gap-2 text-sm mb-2 cursor-pointer">
                    <input type="checkbox" checked={on} onChange={() => toggleFixed(it.key)} data-testid={`rj-fx-${it.key}`} className="w-4 h-4 accent-emerald-600" />
                    <span className="font-medium">{it.label}</span>
                  </label>
                  <input type="number" step="0.01" disabled={!on} value={val ?? ""} onChange={(e) => setFixed(it.key, e.target.value)}
                    placeholder="0 ₺" data-testid={`rj-fx-amt-${it.key}`} className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm disabled:bg-slate-50 disabled:text-slate-400" />
                </div>
              );
            })}
          </div>
        </div>

        {error && <div className="mt-6 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3" data-testid="rj-error">{error}</div>}
        <div className="mt-8 flex justify-end pt-6 border-t border-slate-100">
          <button type="submit" disabled={saving} data-testid="rj-save" className="rounded-full bg-emerald-800 hover:bg-emerald-700 text-white px-8 py-2.5 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
            <Save size={14} /> {saving ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </div>
      </form>
    </FinanceLayout>
  );
}
